"use client"

import { useAuth } from "../contexts/AuthContext"

export default function AuthDebugPanel() {
  const { isTokenExpired, user, hasRefreshError, getTokenDebug } = useAuth()
  const tokenDebug = getTokenDebug()

  // Only show debug panel in Next.js development mode
  if (process.env.NODE_ENV !== "development") {
    return null
  }

  // Only show debug panel for users with edit_rates permission or if token is expired or refresh failed
  if (!user?.edit_rates && !isTokenExpired && !hasRefreshError) {
    return null
  }

  // Show error if token refresh failed
  if (hasRefreshError) {
    return (
      <div className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-4">
        <h2 className="text-lg font-semibold text-red-800">🔄 Token Refresh Failed - User Logged Out</h2>
        <div className="mt-2 space-y-1 text-sm text-red-700">
          <div>
            <strong>Auth Status:</strong> <span className="text-red-600">NOT AUTHENTICATED</span>
          </div>
          <div>
            <strong>Refresh Status:</strong> <span className="text-red-600">FAILED</span>
          </div>
          <div className="mt-2 text-xs">
            ℹ️ The refresh token failed to renew the access token. User needs to log in again.
          </div>
        </div>
      </div>
    )
  }

  // Show warning if token is expired but session still exists
  if (isTokenExpired) {
    return (
      <div className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-4">
        <h2 className="text-lg font-semibold text-red-800">⚠️ Token Expired - User Logged Out</h2>
        <div className="mt-2 space-y-1 text-sm text-red-700">
          <div>
            <strong>Auth Status:</strong> <span className="text-red-600">NOT AUTHENTICATED</span>
          </div>
          <div>
            <strong>Token Status:</strong> <span className="text-red-600">EXPIRED</span>
          </div>
          {tokenDebug && (
            <>
              <div>
                <strong>Expired:</strong> {tokenDebug.expiresAt}
              </div>
              <div>
                <strong>Seconds past expiry:</strong> {Math.abs(tokenDebug.secondsUntilExpiry || 0)}
              </div>
            </>
          )}
          <div className="mt-2 text-xs">
            ℹ️ User appears logged out because backend token is expired, even though NextAuth session exists.
          </div>
        </div>
      </div>
    )
  }

  // Show debug info for authenticated users with edit permissions
  return (
    <div className="mb-4 rounded-lg border-2 border-green-400 bg-green-50 p-4">
      <h2 className="text-lg font-semibold text-green-800">✅ Authentication Debug Info</h2>
      <div className="mt-2 space-y-1 text-sm text-green-700">
        <div>
          <strong>Auth Status:</strong> <span className="text-green-600">AUTHENTICATED</span>
        </div>
        <div>
          <strong>Token Status:</strong> <span className="text-green-600">VALID</span>
        </div>
        <div>
          <strong>User:</strong> {user?.name || user?.email}
        </div>
        <div>
          <strong>Can Edit Rates:</strong> {user?.edit_rates ? "Yes" : "No"}
        </div>
        {tokenDebug && (
          <>
            <div>
              <strong>Expires:</strong> {tokenDebug.expiresAt}
            </div>
            <div>
              <strong>Seconds until expiry:</strong> {tokenDebug.secondsUntilExpiry}
            </div>
            <div>
              <strong>Token age:</strong> {tokenDebug.tokenAge} seconds
            </div>
          </>
        )}
      </div>
    </div>
  )
}
